import { useState, useSyncExternalStore } from 'react';
import { Button, SheetGrip } from '@/components/ui';
import { OtpDialog } from '@/components/OtpDialog';
import { useAuth } from '@/hooks/AuthProvider';
import { useT } from '@/i18n/react';
import { useProfile } from '@/hooks/ProfileProvider';
import { useBackHandler } from '@/hooks/useBackHandler';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import {
  closeCommunityLockPrompt,
  communityLockPromptOpen,
  subscribeToCommunityLock,
} from '@/lib/emailVerification';
import './otp.css';

/**
 * „Még nem hitelesítetted az e-mail-címedet” — a 7 napos türelmi idő utáni
 * közösségi zárolás barátságos lapja (Geri, #76). A piros hibaszöveg helyett
 * egy gomb: a „Hitelesítés most” kiküldi a kódot, és rögtön a beviteli ablak
 * jön. Bármely végpont `community_locked` hibájára az app gyökerében nyílik
 * (`src/lib/emailVerification.ts`).
 */
export function CommunityLockPrompt() {
  const t = useT();
  const open = useSyncExternalStore(subscribeToCommunityLock, communityLockPromptOpen, communityLockPromptOpen);
  const { user, changeEmail } = useAuth();
  const { patchProfile } = useProfile();
  const [otpOpen, setOtpOpen] = useState(false);

  if (!open || !user?.email) return null;

  if (otpOpen) {
    return (
      <OtpDialog
        email={user.email}
        onChangeEmail={changeEmail}
        onClose={() => {
          setOtpOpen(false);
          closeCommunityLockPrompt();
        }}
        onVerified={() => {
          setOtpOpen(false);
          closeCommunityLockPrompt();
          // Csendes frissítés, mint a Beállításokban — a szerver már elmentette.
          patchProfile({ emailVerified: true });
          void user.reload();
        }}
      />
    );
  }

  return <LockSheet t={t} onVerify={() => setOtpOpen(true)} onClose={closeCommunityLockPrompt} />;
}

function LockSheet({
  t,
  onVerify,
  onClose,
}: {
  t: ReturnType<typeof useT>;
  onVerify: () => void;
  onClose: () => void;
}) {
  useBackHandler(true, onClose);
  const panelRef = useSwipeToClose<HTMLDivElement>(onClose);

  return (
    <div className="otp__backdrop" role="dialog" aria-modal="true" aria-label={t.emailVerification.dialogLabel}>
      <div ref={panelRef} className="otp__sheet">
        <SheetGrip />
        <div className="otp__head">
          <h2 className="otp__title">{t.emailVerification.lockPrompt.title}</h2>
          <button type="button" className="otp__close" aria-label={t.common.close} onClick={onClose}>
            ✕
          </button>
        </div>
        <Button block onClick={onVerify}>
          {t.emailVerification.lockPrompt.verifyNow}
        </Button>
        <p className="otp__hint">
          {t.emailVerification.lockPrompt.hint}
        </p>
      </div>
    </div>
  );
}
